import type {
  StoredTypingSession,
} from "../storage";
import type {
  TypingSessionSummary,
} from "../measurement";
import {
  getProfessionalContentPack,
  normalizeProfessionalCustomText,
  PROFESSIONAL_CONTENT_PACKS,
} from "./content";
import type {
  ComparableProfessionalTrend,
  PlannedProfessionalContentItem,
  ProfessionalAssistanceSettings,
  ProfessionalPreset,
  ProfessionalResultNarrative,
  ProfessionalSessionConfig,
  ProfessionalSessionPlan,
  ProfessionalStoredContext,
} from "./types";

export const PROFESSIONAL_GENERATOR_VERSION =
  "professional-generator-1.2.0" as const;

export const DEFAULT_PROFESSIONAL_ASSISTANCE: ProfessionalAssistanceSettings = {
  highContrast: false,
  reducedMotion: false,
  fontScale: 1,
  lineCount: 3,
  audioGuidance: false,
  virtualKeyboard: false,
  relaxedTiming: false,
  liveMetrics: true,
  whitespaceVisible: false,
  caretStyle: "line",
};

export const PROFESSIONAL_PRESETS: readonly ProfessionalPreset[] = [
  {
    id: "daily-recommendation",
    title: "Daily recommendation",
    description: "One click into the highest-value skill supported by your evidence.",
    activityType: "quick",
    contentPackId: "prose-flow",
    completionMode: "timed",
    durationSeconds: 180,
    accuracyTarget: 96,
    strictness: "free-correction",
    focusSkills: ["recommended"],
    difficulty: "standard",
  },
  {
    id: "accuracy-reset",
    title: "Accuracy reset",
    description: "Three calm minutes with a 97% accuracy target.",
    activityType: "focused",
    contentPackId: "prose-flow",
    completionMode: "timed",
    durationSeconds: 180,
    accuracyTarget: 97,
    strictness: "stop-on-word",
    focusSkills: ["accuracy"],
    difficulty: "foundation",
  },
  {
    id: "flow-builder",
    title: "Flow builder",
    description: "Five minutes of readable prose for rhythm and continuity.",
    activityType: "free",
    contentPackId: "prose-flow",
    completionMode: "timed",
    durationSeconds: 300,
    accuracyTarget: 95,
    strictness: "free-correction",
    focusSkills: ["rhythm"],
    difficulty: "standard",
  },
  {
    id: "weak-keys",
    title: "Weak keys",
    description: "An adaptive focused plan using current mastery evidence.",
    activityType: "focused",
    contentPackId: "prose-flow",
    completionMode: "timed",
    durationSeconds: 300,
    accuracyTarget: 96,
    strictness: "free-correction",
    focusSkills: ["weak-keys"],
    difficulty: "standard",
  },
  {
    id: "shift-punctuation",
    title: "Shift & punctuation",
    description: "Capitals and paired symbols without sacrificing rhythm.",
    activityType: "focused",
    contentPackId: "shift-punctuation",
    completionMode: "passage",
    durationSeconds: 180,
    accuracyTarget: 96,
    strictness: "stop-on-word",
    focusSkills: ["shift", "punctuation"],
    difficulty: "advanced",
  },
  {
    id: "numbers-dates",
    title: "Numbers & dates",
    description: "Practical number-row work for data entry and reporting.",
    activityType: "focused",
    contentPackId: "numbers-data",
    completionMode: "passage",
    durationSeconds: 180,
    accuracyTarget: 96,
    strictness: "stop-on-word",
    focusSkills: ["numbers", "dates"],
    difficulty: "advanced",
  },
  {
    id: "coding-symbols",
    title: "Coding symbols",
    description: "Whitespace-aware JavaScript with braces and operators.",
    activityType: "focused",
    contentPackId: "coding-symbols",
    completionMode: "passage",
    durationSeconds: 300,
    accuracyTarget: 95,
    strictness: "stop-on-error",
    focusSkills: ["symbols", "indentation"],
    difficulty: "advanced",
  },
  {
    id: "one-minute-benchmark",
    title: "One-minute benchmark",
    description: "A locked, comparable 60-second benchmark.",
    activityType: "benchmark",
    contentPackId: "prose-flow",
    completionMode: "timed",
    durationSeconds: 60,
    accuracyTarget: 95,
    strictness: "free-correction",
    focusSkills: ["benchmark"],
    difficulty: "standard",
    benchmarkDefinitionId: "standard-prose-en-us-60-v2",
  },
  {
    id: "long-form-endurance",
    title: "Long-form endurance",
    description: "Ten minutes of controlled, sustained prose.",
    activityType: "free",
    contentPackId: "prose-flow",
    completionMode: "timed",
    durationSeconds: 600,
    accuracyTarget: 95,
    strictness: "free-correction",
    focusSkills: ["endurance"],
    difficulty: "advanced",
  },
  {
    id: "game-warm-up",
    title: "Game warm-up",
    description: "Short bursts and punctuation before entering Wordfall Arena.",
    activityType: "focused",
    contentPackId: "chat-commands",
    completionMode: "passage",
    durationSeconds: 120,
    accuracyTarget: 95,
    strictness: "free-correction",
    focusSkills: ["game-readiness"],
    difficulty: "standard",
  },
] as const;

function hashText(value: string): string {
  return [...value]
    .reduce(
      (hash, character) =>
        (hash * 31 + (character.codePointAt(0) ?? 0)) >>> 0,
      17,
    )
    .toString(36);
}

function hashNumber(value: string): number {
  return [...value].reduce(
    (hash, character) =>
      (Math.imul(hash ^ (character.codePointAt(0) ?? 0), 16_777_619)) >>> 0,
    2_166_136_261,
  );
}

function seededOrder<T>(items: readonly T[], seed: string): T[] {
  if (!seed || items.length < 2) return [...items];
  const ordered = [...items];
  let state = hashNumber(seed) || 1;
  for (let index = ordered.length - 1; index > 0; index -= 1) {
    state = (Math.imul(state, 1_664_525) + 1_013_904_223) >>> 0;
    const swapIndex = state % (index + 1);
    [ordered[index], ordered[swapIndex]] = [
      ordered[swapIndex]!,
      ordered[index]!,
    ];
  }
  return ordered;
}

function durationBand(seconds: number): string {
  if (seconds <= 30) return "30s";
  if (seconds <= 60) return "60s";
  if (seconds <= 120) return "120s";
  if (seconds <= 180) return "180s";
  if (seconds <= 300) return "5m";
  if (seconds <= 600) return "10m";
  return "extended";
}

function assistanceSignature(
  assistance: ProfessionalAssistanceSettings,
): string {
  return [
    assistance.audioGuidance ? "audio" : "silent",
    assistance.virtualKeyboard ? "keyboard" : "no-keyboard",
    assistance.relaxedTiming ? "relaxed" : "standard-timing",
  ].join(":");
}

function comparisonKeyFor(
  config: ProfessionalSessionConfig,
  contentPackVersion: string,
  benchmarkDefinitionId?: string,
): string {
  const contentIdentity =
    config.activityType === "custom"
      ? `custom-${hashText(normalizeProfessionalCustomText(config.customText ?? ""))}`
      : benchmarkDefinitionId ?? "practice";
  return [
    config.activityType,
    config.contentPackId,
    durationBand(config.durationSeconds),
    config.strictness,
    config.keyboardLayout,
    assistanceSignature(config.assistance),
    contentIdentity,
    contentPackVersion,
    "measurement-2",
  ].join("|");
}

function exposureCount(text: string, focus: string): number {
  const normalized = focus
    .replace(/^(?:key|shiftedKey|bigram|trigram|punctuation|number):/, "")
    .replace("space", " ");
  if (
    normalized.length === 0 ||
    [
      "recommended",
      "accuracy",
      "rhythm",
      "weak-keys",
      "benchmark",
      "numbers",
      "dates",
      "symbols",
      "indentation",
      "endurance",
      "game-readiness",
    ].includes(normalized)
  ) {
    return 0;
  }
  let count = 0;
  let index = 0;
  while (index <= text.length - normalized.length) {
    const found = text.indexOf(normalized, index);
    if (found < 0) break;
    count += 1;
    index = found + 1;
  }
  return count;
}

function plannedItems(
  config: ProfessionalSessionConfig,
): PlannedProfessionalContentItem[] {
  const custom = normalizeProfessionalCustomText(config.customText ?? "");
  if (config.activityType === "custom" && custom) {
    return [
      {
        id: `custom-${hashText(custom)}`,
        version: "private-local-v1",
        packId: "custom",
        category: "prose",
        text: custom,
        focusSkills: [...config.focusSkills],
        benchmarkApproved: false,
      },
    ];
  }

  const recommendation = normalizeProfessionalCustomText(
    config.recommendationText ?? "",
  );
  if (recommendation) {
    return [
      {
        id: config.recommendationId ?? `recommendation-${hashText(recommendation)}`,
        version: "adaptive-local-v1",
        packId: "adaptive",
        category: "prose",
        text: recommendation,
        focusSkills: [...config.focusSkills],
        benchmarkApproved: false,
      },
    ];
  }

  const fallbackPack =
    getProfessionalContentPack("prose-flow") ?? PROFESSIONAL_CONTENT_PACKS[0]!;
  const pack = getProfessionalContentPack(config.contentPackId) ?? fallbackPack;
  const candidates =
    config.activityType === "benchmark"
      ? pack.items.filter((item) => item.benchmarkApproved)
      : pack.items.filter(
          (item) => {
            if (config.difficulty === "foundation") {
              return item.difficulty === "foundation";
            }
            if (config.difficulty === "standard") {
              return item.difficulty !== "advanced";
            }
            return true;
          },
        );
  const preferred = candidates.length > 0 ? candidates : pack.items;
  const preferredIds = new Set(preferred.map((item) => item.id));
  const overflow = pack.items.filter((item) => !preferredIds.has(item.id));
  const ordered =
    config.activityType === "benchmark"
      ? [...preferred]
      : [
          ...seededOrder(
            preferred,
            `${pack.id}|${config.contentRotationSeed ?? ""}`,
          ),
          ...seededOrder(
            overflow,
            `${pack.id}|${config.contentRotationSeed ?? ""}|overflow`,
          ),
        ];
  const recentIds = new Set(config.recentContentIds ?? []);
  const prioritized =
    config.activityType === "benchmark"
      ? ordered
      : [
          ...ordered.filter((item) => !recentIds.has(item.id)),
          ...ordered.filter((item) => recentIds.has(item.id)),
        ];
  const separator =
    pack.category === "code" || pack.category === "structured-data"
      ? "\n\n"
      : " ";
  const targetLength =
    config.completionMode === "timed"
      ? Math.ceil(config.durationSeconds * 8.5)
      : 450;
  const selected: typeof prioritized = [];
  let selectedLength = 0;
  for (const item of prioritized) {
    selected.push(item);
    selectedLength += item.text.trim().length + separator.length;
    if (selectedLength >= targetLength) {
      break;
    }
  }
  return selected.map((item) => ({
    id: item.id,
    version: item.version,
    packId: pack.id,
    category: pack.category,
    text: item.text,
    focusSkills: [...item.focusSkills],
    benchmarkApproved: item.benchmarkApproved,
    language: item.language,
  }));
}

function buildSourceText(
  items: readonly PlannedProfessionalContentItem[],
): string {
  const separator =
    items[0]?.category === "code" ||
    items[0]?.category === "structured-data"
      ? "\n\n"
      : " ";
  return items.map((item) => item.text.trim()).join(separator);
}

export function getProfessionalPreset(
  presetId: string,
): ProfessionalPreset {
  return (
    PROFESSIONAL_PRESETS.find((preset) => preset.id === presetId) ??
    PROFESSIONAL_PRESETS[0]!
  );
}

export function configFromProfessionalPreset(
  presetId: string,
  overrides: Partial<ProfessionalSessionConfig> = {},
): ProfessionalSessionConfig {
  const preset = getProfessionalPreset(presetId);
  return {
    presetId: preset.id,
    activityType: preset.activityType,
    contentPackId: preset.contentPackId,
    completionMode: preset.completionMode,
    durationSeconds: preset.durationSeconds,
    wordCount: preset.wordCount,
    accuracyTarget: preset.accuracyTarget,
    strictness: preset.strictness,
    focusSkills: [...preset.focusSkills],
    difficulty: preset.difficulty,
    punctuation: true,
    numbers: preset.contentPackId === "numbers-data",
    capitalization: true,
    keyboardLayout: "us-qwerty",
    ...overrides,
    assistance: {
      ...DEFAULT_PROFESSIONAL_ASSISTANCE,
      ...overrides.assistance,
    },
  };
}

export function buildProfessionalSessionPlan(
  input: ProfessionalSessionConfig,
): ProfessionalSessionPlan {
  const preset = getProfessionalPreset(input.presetId);
  const config: ProfessionalSessionConfig = {
    ...input,
    durationSeconds: Math.max(30, Math.min(900, input.durationSeconds)),
    accuracyTarget: Math.max(80, Math.min(100, input.accuracyTarget)),
    focusSkills: [...new Set(input.focusSkills)].slice(0, 3),
    contentRotationSeed: String(input.contentRotationSeed ?? "").slice(0, 128),
    recentContentIds: [
      ...new Set(
        (input.recentContentIds ?? [])
          .filter((id): id is string => typeof id === "string")
          .map((id) => id.slice(0, 160)),
      ),
    ].slice(0, 4_000),
    assistance: {
      ...DEFAULT_PROFESSIONAL_ASSISTANCE,
      ...input.assistance,
      fontScale: Math.max(0.85, Math.min(1.6, input.assistance.fontScale)),
      lineCount: Math.max(2, Math.min(6, input.assistance.lineCount)),
    },
  };
  const warnings: string[] = [];
  if (config.strictness === "stop-on-error") {
    warnings.push(
      "Stop on error can add motor pressure; free correction is the accessible default.",
    );
  }
  if (config.activityType === "custom" && !config.customText?.trim()) {
    throw new Error("Custom content requires text before a session can start.");
  }
  if (config.activityType === "benchmark") {
    config.contentPackId = "prose-flow";
    config.completionMode = "timed";
    config.strictness = "free-correction";
    config.durationSeconds = [30, 60, 120, 180].includes(config.durationSeconds)
      ? config.durationSeconds
      : 60;
  }
  const contentItems = plannedItems(config);
  const sourceText = buildSourceText(contentItems);
  if (sourceText.length < 20) {
    throw new Error("The selected content is too short for a useful session.");
  }
  const benchmarkDefinitionId =
    config.activityType === "benchmark"
      ? `standard-prose-en-us-${config.durationSeconds}-v2`
      : undefined;
  const selectedPack = getProfessionalContentPack(config.contentPackId);
  const fallbackPack =
    getProfessionalContentPack("prose-flow") ?? PROFESSIONAL_CONTENT_PACKS[0]!;
  const contentPackVersion =
    config.activityType === "custom"
      ? "private-local-v1"
      : config.recommendationText
        ? "adaptive-local-v1"
        : selectedPack?.version ?? fallbackPack.version;
  const contentStatus =
    config.activityType === "custom"
      ? "private"
      : config.recommendationText
        ? "adaptive"
        : selectedPack?.status ?? fallbackPack.status;
  const requestedCharacterEstimate = Math.ceil(config.durationSeconds * 8.5);
  const uniqueDurationEstimateSeconds = Math.max(
    30,
    Math.floor(sourceText.length / 8.5),
  );
  if (
    config.completionMode === "timed" &&
    config.activityType !== "benchmark" &&
    sourceText.length < requestedCharacterEstimate
  ) {
    const estimatedDuration =
      uniqueDurationEstimateSeconds < 60
        ? `${uniqueDurationEstimateSeconds} seconds`
        : `${Math.max(1, Math.round(uniqueDurationEstimateSeconds / 60))} minute${Math.round(uniqueDurationEstimateSeconds / 60) === 1 ? "" : "s"}`;
    warnings.push(
      `This content pack currently provides about ${estimatedDuration} of unique material at the reference pace; the session ends when the passage is complete.`,
    );
  }
  const targetExposure = Object.fromEntries(
    config.focusSkills.map((focus) => [focus, exposureCount(sourceText, focus)]),
  );
  const comparisonKey = comparisonKeyFor(
    config,
    contentPackVersion,
    benchmarkDefinitionId,
  );
  const recordEligible =
    config.activityType !== "custom" &&
    (config.activityType !== "benchmark" ||
      contentItems.every((item) => item.benchmarkApproved));
  const identity = hashText(
    [
      config.presetId,
      config.activityType,
      config.contentPackId,
      sourceText.slice(0, 160),
      comparisonKey,
    ].join("|"),
  );

  return {
    id: `professional-${identity}`,
    activityType: config.activityType,
    preset: {
      ...preset,
      activityType: config.activityType,
      contentPackId: config.contentPackId,
      completionMode: config.completionMode,
      durationSeconds: config.durationSeconds,
      accuracyTarget: config.accuracyTarget,
      strictness: config.strictness,
      focusSkills: [...config.focusSkills],
      difficulty: config.difficulty,
      benchmarkDefinitionId,
    },
    config,
    contentItems,
    sourceText,
    targetExposure,
    expectedDurationSeconds:
      config.completionMode === "timed"
        ? Math.min(config.durationSeconds, uniqueDurationEstimateSeconds)
        : config.durationSeconds,
    benchmarkDefinitionId,
    comparisonKey,
    generatorVersion: PROFESSIONAL_GENERATOR_VERSION,
    contentPackVersion,
    contentStatus,
    contentApproved:
      config.activityType !== "benchmark" ||
      contentItems.every((item) => item.benchmarkApproved),
    recordEligible,
    warnings,
  };
}

export function buildProfessionalStoredContext(
  plan: ProfessionalSessionPlan,
): ProfessionalStoredContext {
  return {
    mode: "professional",
    contentId: plan.contentItems.map((item) => item.id).join(","),
    contentPackId: plan.config.contentPackId,
    activityType: plan.activityType,
    presetId: plan.config.presetId,
    strictness: plan.config.strictness,
    keyboardLayout: plan.config.keyboardLayout,
    completionMode: plan.config.completionMode,
    durationBand: durationBand(plan.config.durationSeconds),
    benchmarkDefinitionId: plan.benchmarkDefinitionId,
    comparisonKey: plan.comparisonKey,
    generatorVersion: plan.generatorVersion,
    contentPackVersion: plan.contentPackVersion,
    assistanceSignature: assistanceSignature(plan.config.assistance),
    recordKind: "session",
  };
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const ordered = [...values].sort((left, right) => left - right);
  const middle = Math.floor(ordered.length / 2);
  return ordered.length % 2 === 0
    ? (ordered[middle - 1]! + ordered[middle]!) / 2
    : ordered[middle]!;
}

export function comparableProfessionalTrend(
  sessions: readonly StoredTypingSession[],
  plan: ProfessionalSessionPlan,
  currentSummary: TypingSessionSummary,
): ComparableProfessionalTrend {
  const comparable = sessions.filter(
    (session) =>
      session.context.mode === "professional" &&
      "comparisonKey" in session.context &&
      session.context.comparisonKey === plan.comparisonKey &&
      session.summary.id !== currentSummary.id &&
      session.summary.validity.isValidPractice,
  );
  const speeds = comparable
    .map((session) => session.summary.accurateWpm)
    .filter((value): value is number => value !== null);
  const accuracies = comparable
    .map((session) => session.summary.keystrokeAccuracy)
    .filter((value): value is number => value !== null);
  const medianAccurateWpm = median(speeds);
  const currentIsComparable =
    plan.activityType === "benchmark"
      ? currentSummary.validity.isValidBenchmark
      : currentSummary.validity.isValidPractice;

  return {
    comparisonKey: plan.comparisonKey,
    sampleCount: comparable.length,
    medianAccurateWpm,
    medianAccuracy: median(accuracies),
    currentAccurateWpm: currentIsComparable
      ? currentSummary.accurateWpm
      : null,
    deltaAccurateWpm:
      !currentIsComparable ||
      medianAccurateWpm === null ||
      currentSummary.accurateWpm === null
        ? null
        : Math.round((currentSummary.accurateWpm - medianAccurateWpm) * 10) /
          10,
  };
}

export function buildProfessionalResultNarrative(
  summary: TypingSessionSummary,
  plan: ProfessionalSessionPlan,
): ProfessionalResultNarrative {
  const wpm =
    summary.accurateWpm === null ? "an unverified pace" : `${Math.round(summary.accurateWpm)} accurate WPM`;
  const accuracy =
    summary.keystrokeAccuracy === null
      ? "insufficient accuracy evidence"
      : `${Math.round(summary.keystrokeAccuracy)}% keystroke accuracy`;
  const primaryFocus = plan.config.focusSkills[0] ?? "general fluency";
  const evidence = [
    {
      label: "Focus",
      value: primaryFocus.replaceAll("-", " "),
      detail: `${plan.contentItems.length} curated content block${plan.contentItems.length === 1 ? "" : "s"}`,
    },
    {
      label: "Corrections",
      value: String(summary.correctedInitialErrors),
      detail: `${summary.uncorrectedErrors} unresolved at completion`,
    },
    {
      label: "Evidence",
      value: String(summary.printableAttempts),
      detail: `${Math.round(summary.activeDurationMs / 1000)} active seconds`,
    },
  ];

  if (!summary.validity.isValidPractice) {
    return {
      headline: "Useful attempt, but more evidence is needed.",
      detail: `You reached ${wpm} with ${accuracy}. This result remains diagnostic and did not update verified progress.`,
      nextAction: "repeat",
      nextActionLabel: "Repeat this session",
      evidence,
    };
  }
  if ((summary.keystrokeAccuracy ?? 0) < plan.config.accuracyTarget) {
    return {
      headline: "Accuracy is the next leverage point.",
      detail: `You held ${wpm} with ${accuracy}. Repeat the same focus at a calmer pace before adding difficulty.`,
      nextAction: "repeat",
      nextActionLabel: "Repeat with the same focus",
      evidence,
    };
  }
  if (summary.validity.isValidBenchmark) {
    return {
      headline: "Verified benchmark complete.",
      detail: `You held ${accuracy} at ${wpm} under the locked ${plan.config.durationSeconds}-second benchmark definition.`,
      nextAction: "vary",
      nextActionLabel: "Vary the practice context",
      evidence,
    };
  }
  return {
    headline: "Control held at the target accuracy.",
    detail: `You held ${accuracy} at ${wpm}. Add variation while keeping the same focus skill visible.`,
    nextAction: "advance",
    nextActionLabel: "Advance the difficulty",
    evidence,
  };
}
